/**
 * Your account: the two things anybody signed in may change about themselves.
 *
 * Open to every role, Viewers included. That is not the recorded exception the
 * feedback button is: a person's own password is not the business data a
 * read-only role guards, and a role that cannot rotate its own credentials is a
 * role whose credentials never get rotated.
 *
 * **One dialog, two independent saves.** Renaming yourself and changing your
 * password are separate acts with separate refusals, and a single Save covering
 * both would have to explain which half of it failed. Each section states its
 * own outcome where it happened.
 *
 * **The new password is typed twice**, which the server neither sends nor
 * checks. The second box is a typo check and belongs on this side: the
 * characters are masked, so the person cannot see what they typed, and there is
 * no administrator reset anywhere in this product (item 38's scope, and
 * deliberate: a reset endpoint is a way to take over somebody's account). So an
 * unnoticed typo here is an account nobody inside the system can rescue. The
 * confirmation is not sent to the server, because a second copy of a secret on
 * the wire buys nothing that this check has not already bought.
 */

import { CircleCheck } from "lucide-react"
import * as React from "react"

import { Modal } from "@/components/modal"
import { Button, Field, FormError, Input } from "@/components/ui"
import { displayName, type Me } from "@/lib/session"
import { fieldErrorsFrom, formErrorFrom, type FieldErrors } from "@/lib/validation-errors"

import { useChangePassword, useChangeUsername } from "./api"

export function AccountSettingsDialog({
  me,
  onClose,
  onDone,
}: {
  me: Me
  onClose: () => void
  /** Fired with each outcome, so the chrome can announce it politely. */
  onDone: (message: string) => void
}) {
  return (
    <Modal
      title="Your account"
      description={`Signed in as ${displayName(me)}. Changes here affect only your own account.`}
      size="md"
      onClose={onClose}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <UsernameSection me={me} onDone={onDone} />
      <div className="my-5 border-t" />
      <PasswordSection onDone={onDone} />
    </Modal>
  )
}

function UsernameSection({ me, onDone }: { me: Me; onDone: (message: string) => void }) {
  const [username, setUsername] = React.useState(me.username)
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [formError, setFormError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)
  const change = useChangeUsername()

  function save() {
    setErrors({})
    setFormError(null)
    setSaved(false)
    change.mutate(username, {
      onSuccess: (updated) => {
        // The hook writes the answer into the session query, so the name in
        // the top bar changes as this line is read.
        setUsername(updated.username)
        setSaved(true)
        onDone("Your username has been changed.")
      },
      onError: (cause: unknown) => {
        setErrors(fieldErrorsFrom(cause))
        setFormError(
          formErrorFrom(cause, "Your username could not be changed. Please try again."),
        )
      },
    })
  }

  return (
    <section aria-labelledby="account-username">
      <h3 id="account-username" className="mb-1 text-sm font-bold">
        Username
      </h3>
      <p className="mb-3 text-sm text-muted-foreground">
        The name you type when you sign in. Everything you have already done keeps
        the name it was done under.
      </p>

      {formError && <FormError className="mb-4">{formError}</FormError>}

      <Field label="Username" error={errors.username} required>
        <Input
          value={username}
          onChange={(event) => {
            setUsername(event.target.value)
            setSaved(false)
          }}
          autoComplete="username"
        />
      </Field>

      <div className="flex items-center gap-3">
        <Button
          onClick={save}
          loading={change.isPending}
          disabled={username.trim() === me.username}
        >
          Save username
        </Button>
        {saved && <Done>Username changed.</Done>}
      </div>
    </section>
  )
}

function PasswordSection({ onDone }: { onDone: (message: string) => void }) {
  const [current, setCurrent] = React.useState("")
  const [next, setNext] = React.useState("")
  const [again, setAgain] = React.useState("")
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [formError, setFormError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)
  const change = useChangePassword()

  function save() {
    setErrors({})
    setFormError(null)
    setSaved(false)

    // The one rule this side owns, for the reason in the file's own docstring.
    // Checked before the request, because it is not the server's rule and it
    // must not read as one.
    if (next !== again) {
      setErrors({ again: "The two passwords do not match." })
      return
    }

    change.mutate(
      { current_password: current, new_password: next },
      {
        onSuccess: () => {
          setCurrent("")
          setNext("")
          setAgain("")
          setSaved(true)
          onDone("Your password has been changed. You are still signed in.")
        },
        onError: (cause: unknown) => {
          setErrors(fieldErrorsFrom(cause))
          setFormError(
            formErrorFrom(
              cause,
              "Your password could not be changed. Please try again.",
            ),
          )
        },
      },
    )
  }

  return (
    <section aria-labelledby="account-password">
      <h3 id="account-password" className="mb-1 text-sm font-bold">
        Password
      </h3>
      <p className="mb-3 text-sm text-muted-foreground">
        Nobody else can change or look up your password, so keep the new one
        somewhere safe. You stay signed in on this device.
      </p>

      {formError && <FormError className="mb-4">{formError}</FormError>}

      <Field label="Current password" error={errors.current_password} required>
        <Input
          type="password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          autoComplete="current-password"
        />
      </Field>

      <Field
        label="New password"
        error={errors.new_password}
        hint="At least 8 characters, and not something a stranger would guess."
        required
      >
        <Input
          type="password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          autoComplete="new-password"
        />
      </Field>

      <Field label="Confirm new password" error={errors.again} required>
        <Input
          type="password"
          value={again}
          onChange={(event) => setAgain(event.target.value)}
          autoComplete="new-password"
        />
      </Field>

      <div className="flex items-center gap-3">
        <Button
          onClick={save}
          loading={change.isPending}
          disabled={!current || !next || !again}
        >
          Change password
        </Button>
        {saved && <Done>Password changed.</Done>}
      </div>
    </section>
  )
}

/** The quiet confirmation beside a section's own button. */
function Done({ children }: { children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5 text-sm text-success">
      <CircleCheck className="h-4 w-4" aria-hidden />
      {children}
    </span>
  )
}
